/**
 * UserDetail Component
 *
 * Administrative interface for viewing individual user account details.
 * Provides comprehensive view of user data with admin management capabilities.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User as UserIcon, Settings, Shield, TrendingUp, KeyRound } from 'lucide-react';

import { AdminLayout } from '@/components/shared/layouts';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Chip,
  Spinner,
  Divider,
  Accordion,
  AccordionItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  useDisclosure,
  useToastNotification
} from '@enfomentis/ui';
import { userService } from '@/services/UserService';
// REMOVED: tokenService import - users no longer have invitation tokens
// import { tokenService } from '@/services/TokenService';
import type {
  User,
  UserStatus
} from '@/types/supabase/User';
// REMOVED: UserToken import - users no longer have invitation tokens
// import type { UserToken } from '@/types/supabase/UserToken';

/**
 * Props for UserDetail component
 */
export interface UserDetailProps {
  /** Optional user ID override (for testing) */
  userId?: string;
}

/**
 * Available reasons for disabling user accounts
 */
const DISABLE_REASONS = [
  { key: 'policy-violation', label: 'Policy Violation' },
  { key: 'security-concern', label: 'Security Concern' },
  { key: 'user-request', label: 'User Request' },
  { key: 'inactivity', label: 'Extended Inactivity' },
  { key: 'other', label: 'Other' }
] as const;

/**
 * User Detail Component
 *
 * Features:
 * - Comprehensive view of individual user account
 * - Organized sections for easy admin review
 * - Mobile-responsive design with proper spacing
 * - Loading states and error handling
 * - Account status management capabilities
 * - Consistent styling with existing admin interface
 *
 * Usage:
 * ```tsx
 * // Via routing: /admin/users/:id
 * <UserDetail />
 *
 * // Direct usage with ID:
 * <UserDetail userId="123" />
 * ```
 */
export const UserDetail: React.FC<UserDetailProps> = ({
  userId: propUserId
}) => {
  const { id: routeUserId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = propUserId || routeUserId;

  // State management
  const [user, setUser] = useState<User | null>(null);
  // REMOVED: invitationTokens - users no longer have 'invited' status
  // const [invitationTokens, setInvitationTokens] = useState<UserToken[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const [isPasswordResetLoading, setIsPasswordResetLoading] = useState<boolean>(false);
  // REMOVED: isInvitationLoading - users no longer have 'invited' status
  // const [isInvitationLoading, setIsInvitationLoading] = useState<boolean>(false);
  const [expandedAccordionKeys, setExpandedAccordionKeys] = useState<Set<string>>(new Set());

  // Disable confirmation modal state
  const { isOpen: isDisableModalOpen, onOpen: openDisableModal, onClose: closeDisableModal } = useDisclosure();
  const [selectedDisableReason, setSelectedDisableReason] = useState<string>('');
  const [isConfirmingDisable, setIsConfirmingDisable] = useState<boolean>(false);

  // Modal state for future user actions (unused for now)
  // const { isOpen: isActionModalOpen, onOpen: openActionModal, onClose: closeActionModal } = useDisclosure();

  // Toast notifications
  const { showToast } = useToastNotification();

  // Load user data
  const loadUser = useCallback(async (): Promise<void> => {
    if (!userId) {
      setError('No user ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load user data and invitation tokens in parallel
      // REMOVED: invitation tokens fetching - users no longer have invitation tokens
      // const [userData, tokens] = await Promise.all([
      //   userService.get(userId),
      //   tokenService.getUserTokensByType(userId, 'invitation')
      // ]);
      const userData = await userService.get(userId);

      setUser(userData);
      // setInvitationTokens(tokens); // REMOVED - users no longer have invitation tokens
    } catch (err) {
      console.error('Failed to load user:', err);
      setError('Failed to load user details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Load data on mount and when ID changes
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Status chip component
  const StatusBadge: React.FC<{ status: UserStatus; size?: 'sm' | 'lg' }> = ({ status, size = 'lg' }) => {
    const variants: Record<UserStatus, { color: 'warning' | 'success' | 'danger' | 'primary'; text: string }> = {
      active: { color: 'success', text: 'Active' },
      disabled: { color: 'danger', text: 'Disabled' },
      pending_payment: { color: 'warning', text: 'Pending Payment' }
    };

    const variant = variants[status];
    return (
      <Chip color={variant.color} variant="flat" size={size}>
        {variant.text}
      </Chip>
    );
  };

  // Format date helper
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle back navigation
  const handleBack = (): void => {
    navigate('/admin/users');
  };

  // Handle status change with actual database update (for non-disable actions)
  const handleStatusChange = async (newStatus: UserStatus): Promise<void> => {
    if (!user) return;

    // For disable actions, show confirmation modal instead
    if (newStatus === 'disabled') {
      setSelectedDisableReason(''); // Reset reason selection
      openDisableModal();
      return;
    }

    // For enable/reactivate actions, use the new enableUser method
    if (newStatus === 'active') {
      try {
        setIsUpdatingStatus(true);

        console.log(`Re-enabling user ${user.email}`);

        // Call enableUser Edge Function via service
        const result = await userService.enableUser(user.id);

        // Update local state with new user data
        setUser(result.user);

        // Show different toast based on idempotent reason
        if (result.reason === 'already_enabled') {
          showToast({
            color: 'primary',
            title: 'Already Enabled',
            description: `User account was already active.`
          });
        } else {
          showToast({
            color: 'success',
            title: 'Account Reactivated',
            description: `User account has been reactivated and notification email sent to ${user.email}.`
          });
        }

        console.log('✅ User account reactivated successfully:', result);
      } catch (err) {
        console.error('Failed to reactivate user account:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to reactivate account';

        showToast({
          color: 'danger',
          title: 'Reactivation Failed',
          description: `Failed to reactivate user account. ${errorMessage}`
        });
      } finally {
        setIsUpdatingStatus(false);
      }
      return;
    }

    // Fallback for any other status changes (shouldn't happen with current UI)
    try {
      setIsUpdatingStatus(true);

      console.log(`Changing user ${user.email} status from ${user.status} to ${newStatus}`);

      // Update user status in database
      const updatedUser = await userService.update(user.id, {
        status: newStatus
      });

      // Update local state with new user data
      setUser(updatedUser);

      showToast({
        color: 'success',
        title: 'Status Updated',
        description: `User status successfully changed from ${user.status} to ${newStatus}.`
      });

      console.log('✅ User status updated successfully:', updatedUser);
    } catch (err) {
      console.error('Failed to update user status:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user status';

      showToast({
        color: 'danger',
        title: 'Update Failed',
        description: `Failed to update user status. ${errorMessage}`
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // NOTE: Email sending is now handled by disable-user and enable-user Edge Functions
  // The old sendDisableEmail and sendReactivationEmail functions have been removed
  // since the Edge Functions handle email notifications automatically

  // Handle confirmed disable action with reason and email
  const handleConfirmedDisable = useCallback(async (): Promise<void> => {
    if (!user || !selectedDisableReason) return;

    try {
      setIsConfirmingDisable(true);

      console.log(`Disabling user ${user.email} with reason: ${selectedDisableReason}`);

      // Call disableUser Edge Function via service
      // This will: update status, revoke sessions, send email, log telemetry
      const result = await userService.disableUser(user.id, selectedDisableReason);

      // Update local state with new user data
      setUser(result.user);

      // Show different toast based on idempotent reason
      if (result.reason === 'already_disabled') {
        showToast({
          color: 'primary',
          title: 'Already Disabled',
          description: `User account was already disabled.`
        });
      } else {
        // Show warning if session revocation was only partial
        const sessionNote = result.revocation_status === 'partial'
          ? ' Note: Some sessions may not have been revoked.'
          : '';

        showToast({
          color: 'success',
          title: 'Account Disabled',
          description: `User account has been disabled, sessions revoked, and notification email sent to ${user.email}.${sessionNote}`
        });
      }

      console.log('✅ User account disabled successfully:', result);

      // Close modal
      closeDisableModal();
      setSelectedDisableReason('');
    } catch (err) {
      console.error('Failed to disable user account:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to disable account';

      showToast({
        color: 'danger',
        title: 'Disable Failed',
        description: `Failed to disable user account. ${errorMessage}`
      });
    } finally {
      setIsConfirmingDisable(false);
    }
  }, [user, selectedDisableReason, closeDisableModal, showToast]);

  // Handle password reset for active users
  const handlePasswordReset = useCallback(async (): Promise<void> => {
    if (!user || user.status !== 'active') return;

    try {
      setIsPasswordResetLoading(true);

      const result = await userService.sendPasswordReset(user.id, 'admin');

      showToast({
        color: 'success',
        title: 'Password Reset Sent',
        description: `Password reset email sent to ${user.email}. The user will receive instructions to reset their password.`
      });

      console.log('✅ Password reset sent successfully:', result);
    } catch (err) {
      console.error('Failed to send password reset:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send password reset';

      showToast({
        color: 'danger',
        title: 'Password Reset Failed',
        description: `Failed to send password reset to ${user.email}. ${errorMessage}`
      });
    } finally {
      setIsPasswordResetLoading(false);
    }
  }, [user, showToast]);

  // REMOVED: handleResendInvitation - users no longer have 'invited' status
  // Invitation resending is now handled via InvitationTokenService on beta applications
  /*const handleResendInvitation = useCallback(async (): Promise<void> => {
    // Placeholder - users no longer have 'invited' status
    console.warn('Resend invitation no longer available - users are only created after activation');
    return Promise.resolve();
  }, []);*/

  // Loading state
  if (loading) {
    return (
      <AdminLayout title="Loading User...">
        <div className="flex items-center justify-center py-12">
          <Spinner color="primary" size="lg" />
          <span className="ml-3 text-text-secondary">Loading user details...</span>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <AdminLayout title="User Not Found">
        <div className="text-center py-12">
          <p className="text-danger mb-4">{error || 'User not found'}</p>
          <div className="space-x-4">
            <Button color="primary" onPress={loadUser}>
              Retry
            </Button>
            <Button variant="flat" onPress={handleBack}>
              Back to List
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`${user.fullName} - User Account`}>
      <Card shadow='none' radius="none" fullWidth className="flex flex-col min-h-[calc(100vh-200px)]">
        {/* Header with Back Button and Status */}
        <CardHeader className="flex-shrink-0 flex items-center justify-between flex-wrap gap-4 border-b border-default-200">
          <Button
            variant="flat"
            startContent={<ArrowLeft size={16} />}
            onPress={handleBack}
            className="flex-shrink-0"
          >
            <span className="hidden sm:inline">Back to Users</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center gap-3">
            <Chip variant="bordered" size="sm" color="default">
              ID: {user.id.slice(-8)}
            </Chip>
            <StatusBadge status={user.status} />
          </div>
        </CardHeader>

        {/* Scrollable Content Section */}
        <CardBody className="flex-1 space-y-6 p-6">
          {/* Personal Information Section */}
          <Card shadow="sm" className='flex-shrink-0 border border-default-300'>
            <CardHeader>
              <div className="flex items-center gap-2">
                <UserIcon size={18} className="text-primary" />
                <h3 className="text-lg font-semibold text-text-primary">Account Information</h3>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Full Name</p>
                  <p className="text-text-primary">{user.fullName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Email Address</p>
                  <p className="text-text-primary">{user.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Account Created</p>
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-text-secondary" />
                    <p className="text-text-primary">{formatDate(user.createdAt)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Last Updated</p>
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-text-secondary" />
                    <p className="text-text-primary">{formatDate(user.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Account Status Section */}
          <Card shadow="sm" className='flex-shrink-0 border border-default-300'>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings size={18} className="text-primary" />
                <h3 className="text-lg font-semibold text-text-primary">Account Status</h3>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Current Status</p>
                  <StatusBadge status={user.status} size="sm" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Invitation Sent</p>
                  <p className="text-text-primary">{formatDate(user.invitedAt)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Account Activated</p>
                  <p className="text-text-primary">{formatDate(user.activatedAt)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Onboarding Status</p>
                  <Chip
                    size="sm"
                    variant="flat"
                    color={user.onboardingCompleted ? 'success' : 'warning'}
                  >
                    {user.onboardingCompleted ? 'Completed' : 'Pending'}
                  </Chip>
                </div>
              </div>
              {user.onboardingCompleted && user.onboardingCompletedAt && (
                <>
                  <Divider />
                  <div>
                    <p className="text-sm font-medium text-text-secondary mb-1">Onboarding Completed</p>
                    <div className="flex items-center gap-2">
                      <TrendingUp size={14} className="text-success" />
                      <p className="text-text-primary">{formatDate(user.onboardingCompletedAt)}</p>
                    </div>
                  </div>
                </>
              )}

              {/* REMOVED: invitation tokens display - users no longer have 'invited' status */}
              {/* TODO: Display invitation tokens from invitation_tokens table if needed */}
              {/* Temporarily disabled invitation tokens section
              {invitationTokens.length > 0 && (
                <>
                  <Divider />
                  <div>
                    <p className="text-sm font-medium text-text-secondary mb-2">Active Invitation Token</p>
                    {invitationTokens
                      .filter(token => !token.used_at && new Date(token.expires_at) > new Date())
                      .slice(0, 1) // Show only the most recent active token
                      .map(token => (
                        <div key={token.id} className="bg-primary-50 dark:bg-primary-900/10 p-3 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Mail size={14} className="text-primary" />
                              <span className="text-sm font-medium text-primary">Activation Pending</span>
                            </div>
                            <Chip size="sm" color="primary" variant="flat">Active</Chip>
                          </div>

                          <div className="text-xs space-y-1">
                            <div className="flex justify-between">
                              <span className="text-default-500">Expires:</span>
                              <span className="font-medium">
                                {new Date(token.expires_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-default-500">Token:</span>
                              <span className="font-mono text-xs">{token.token.substring(0, 12)}...</span>
                            </div>
                          </div>

                          <div className="flex gap-1 mt-2">
                            <Button
                              size="sm"
                              variant="flat"
                              color="primary"
                              startContent={<Copy size={12} />}
                              onPress={() => {
                                const activationUrl = `${window.location.origin}/activate/${token.token}`;
                                navigator.clipboard.writeText(activationUrl);
                              }}
                              className="text-xs h-6"
                            >
                              Copy Activation Link
                            </Button>
                            <Button
                              size="sm"
                              variant="flat"
                              color="secondary"
                              startContent={<ExternalLink size={12} />}
                              onPress={() => {
                                const activationUrl = `${window.location.origin}/activate/${token.token}`;
                                window.open(activationUrl, '_blank');
                              }}
                              className="text-xs h-6"
                            >
                              Test
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
              End temporarily disabled invitation tokens section */}
            </CardBody>
          </Card>

          {/* TODO: [CONTRACTOR] Ensure authentication services provide user login history, active session tracking, and security event logging APIs for admin monitoring */}
          {/* TODO: [INTERNAL] Add user activity monitoring section - display login history, active sessions, and security events using contractor's authentication APIs */}
          {/* TODO: [INTERNAL] Add GDPR data export feature - implement user data download as JSON/CSV for legal compliance */}
          {/* TODO: [INTERNAL] Add user security timeline - show account changes, password resets, and security events for admin review */}
        </CardBody>

        <CardFooter className="flex-shrink-0 border-t border-default-400">
          {/* Admin Actions Section - Collapsible */}
          <Accordion
            variant="light"
            fullWidth
            isCompact
            selectedKeys={expandedAccordionKeys}
            onSelectionChange={(keys) => setExpandedAccordionKeys(new Set(Array.from(keys as Set<string>)))}
          >
            <AccordionItem
              key="admin-actions"
              aria-label="Admin Actions"
              title={
                <div className="flex items-center gap-2 pb-2 border-b-1 border-default-100">
                  <Shield size={18} className="text-primary" />
                  <span className="text-lg font-semibold text-primary-800 dark:text-primary-200">
                    Admin Actions
                  </span>
                </div>
              }
            >
              <div className="space-y-4 pb-4 sm:max-h-none max-h-[30vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-1">
                      Current Status
                    </p>
                    <StatusBadge status={user.status} size="sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-1">
                      Quick Actions
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {/* REMOVED: invited user actions - users no longer have 'invited' status */}
                      {user.status === 'active' && (
                        <>
                          <Button
                            size="sm"
                            color="warning"
                            variant="flat"
                            startContent={<KeyRound size={14} />}
                            onPress={handlePasswordReset}
                            isLoading={isPasswordResetLoading}
                          >
                            Pwd Reset
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            variant="flat"
                            onPress={() => handleStatusChange('disabled')}
                            isLoading={isUpdatingStatus}
                          >
                            Disable Account
                          </Button>
                        </>
                      )}
                      {user.status === 'disabled' && (
                        <Button
                          size="sm"
                          color="success"
                          variant="flat"
                          onPress={() => handleStatusChange('active')}
                          isLoading={isUpdatingStatus}
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* TODO: [CONTRACTOR] Ensure authentication system provides login attempt tracking and session management data that admin interfaces can query */}
                {/* TODO: [INTERNAL] Add login history viewer button - implement modal/drawer to display user's login timeline using contractor's APIs */}
                {/* TODO: [INTERNAL] Add GDPR data export button - implement user data download workflow for legal compliance */}
                {/* TODO: [INTERNAL] Add account deletion button with confirmation - implement permanent user data removal workflow (legal requirement) */}

                <Divider />
                <div className="bg-warning-50 dark:bg-warning-900/10 p-3 rounded-md">
                  <p className="text-sm text-warning-700 dark:text-warning-300">
                    <strong>Development Note:</strong> User management actions are currently mock implementations.
                    Contractor integration will add proper user status management, invitation resending,
                    and account security features.
                  </p>
                </div>
              </div>
            </AccordionItem>
          </Accordion>
        </CardFooter>
      </Card>

      {/* Account Disable Confirmation Modal */}
      <Modal
        isOpen={isDisableModalOpen}
        onClose={closeDisableModal}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold">Disable User Account</h2>
            <p className="text-sm text-default-500">
              This action will disable {user?.fullName}'s account and send them a notification email.
            </p>
          </ModalHeader>

          <ModalBody>
            <div className="space-y-4">
              <div className="bg-warning-50 dark:bg-warning-900/20 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-warning-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-warning-800 dark:text-warning-200">
                      Account Disable Confirmation
                    </h4>
                    <p className="text-sm text-warning-700 dark:text-warning-300 mt-1">
                      {user?.fullName} will immediately lose access to their account and receive an email notification explaining the reason for the disable action.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-default-700 mb-2">
                  Reason for Disabling Account <span className="text-danger">*</span>
                </label>
                <Select
                  placeholder="Select a reason for disabling this account"
                  selectedKeys={selectedDisableReason ? [selectedDisableReason] : []}
                  onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys)[0] as string;
                    setSelectedDisableReason(selectedKey || '');
                  }}
                  isRequired
                  variant="bordered"
                >
                  {DISABLE_REASONS.map((reason) => (
                    <SelectItem key={reason.key}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </Select>
              </div>

              <div className="bg-default-100 dark:bg-default-900/50 p-3 rounded-md">
                <p className="text-xs text-default-600">
                  <strong>What happens next:</strong>
                </p>
                <ul className="text-xs text-default-500 mt-1 space-y-1">
                  <li>• User account status will be changed to "disabled"</li>
                  <li>• User will be immediately logged out of all sessions</li>
                  <li>• User will receive an email notification with the reason</li>
                  <li>• Account can be reactivated later from admin actions</li>
                </ul>
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="flat"
              onPress={closeDisableModal}
              isDisabled={isConfirmingDisable}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={handleConfirmedDisable}
              isLoading={isConfirmingDisable}
              isDisabled={!selectedDisableReason}
            >
              Disable Account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
};